// A workshop styled as a small marketplace, described the way age-of-mcp authors
// it: a `stall` helper (posts, counter, awning, wares) tiled into three `stalls`
// plus the shared `foundationSlab` and `scaffold`, walked through its build
// stages. Ported 1:1 from age-of-mcp's workshop.ts.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const POST   = "#6b4a2c";   // stall timber
const TABLE  = "#9a7440";   // counter wood
const POLE   = "#5a4026";   // banner pole
const BARREL = "#8a5d2e";
const GOODS  = ["#e07a3c", "#7aa83c", "#9c4b8c"];   // orange / green / plum wares

const BASE = 0.14;

// One market stall facing +Z: two back posts, a counter, and a sloped cloth
// awning. `level` is the build stage — 1 posts, 2 + counter, 3 + awning & wares.
function stall(cx: number, cz: number, cloth: string, level: number): Part[] {
  const out: Part[] = [];
  for (const dx of [-0.22, 0.22]) {
    out.push({
      geometry: new THREE.BoxGeometry(0.06, 0.6, 0.06), color: POST,
      position: [cx + dx, BASE + 0.3, cz - 0.14],
    });
  }
  if (level >= 2) {
    out.push({
      geometry: new THREE.BoxGeometry(0.56, 0.08, 0.34), color: TABLE,
      position: [cx, BASE + 0.24, cz + 0.06],
    });
  }
  if (level >= 3) {
    // A striped cloth awning, sloping down toward the front of the stall.
    out.push({
      geometry: new THREE.BoxGeometry(0.66, 0.04, 0.5), color: cloth,
      position: [cx, BASE + 0.68, cz - 0.04], rotation: [0.32, 0, 0],
    });
    // Wares laid out along the counter.
    for (let i = 0; i < 3; i++) {
      out.push({
        geometry: new THREE.BoxGeometry(0.12, 0.12, 0.14), color: GOODS[i]!,
        position: [cx - 0.16 + i * 0.16, BASE + 0.36, cz + 0.06],
      });
    }
  }
  return out;
}

// Three stalls spread around the plot, awnings in different colours.
function stalls(level: number): Part[] {
  return [
    ...stall(-0.78, -0.62, "#b8473a", level),   // red
    ...stall( 0.8,  -0.5,  "#3a8a8a", level),   // teal
    ...stall(-0.1,   0.82, "#d2a32e", level),   // gold
  ];
}

// The build stages of the workshop: foundation, stall posts with scaffolding,
// stalls with counters, and the finished market square with awnings, wares, a
// banner pole and a couple of barrels.
function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(2.94)];
  if (s === 1) return [foundationSlab(2.94), ...stalls(1), ...scaffold(2.8, 0.8)];
  if (s === 2) return [foundationSlab(2.94), ...stalls(2), ...scaffold(2.8, 1.05)];

  return [
    foundationSlab(2.94),
    ...stalls(3),
    // A banner pole marking the market square.
    { geometry: new THREE.BoxGeometry(0.07, 1.3, 0.07), color: POLE,
      position: [0.7, BASE + 0.65, 0.1] },
    { geometry: new THREE.BoxGeometry(0.03, 0.3, 0.4), color: "#c0392b",
      position: [0.73, BASE + 1.02, 0.3] },
    // A couple of barrels of stock standing about.
    { geometry: new THREE.CylinderGeometry(0.16, 0.16, 0.36, 8), color: BARREL,
      position: [0.1, BASE + 0.18, 0.0] },
    { geometry: new THREE.CylinderGeometry(0.14, 0.14, 0.32, 8), color: BARREL,
      position: [-0.78, BASE + 0.16, 0.3] },
  ];
}

export const workshop = defineModel({
  id: "workshop",
  tiles: { w: 3, h: 3 },
  batchId: "standard",
  name: "Workshop",
  tags: ["building","workshop","low-poly","age-of-mcp","classic"],
  summary: "Low-poly workshop.",
  description: "A walled crafting compound of benches under a tiled roof.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
